'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Image from 'next/image';
import { useSelector } from 'react-redux';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Stack,
  Paper,
  Chip,
  IconButton,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  CircularProgress,
} from '@mui/material';
import {
  LocalShipping,
  Speed,
  Security,
  TrendingDown,
  CheckCircle,
  ArrowForward,
  PlayArrow,
  Star,
  KeyboardArrowDown,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { brandingConfig } from '@/config/branding';

// Modern color palette
const COLORS = {
  primary: '#000000',
  accent: '#10b981',
  accentLight: '#d1fae5',
  surface: '#f9fafb',
  white: '#ffffff',
  textPrimary: '#111827',
  textSecondary: '#6b7280',
};

export default function LandingPageRevamped() {
  const theme = useTheme();
  const router = useRouter();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isNavigatingToQuote, setIsNavigatingToQuote] = useState(false);

  // Check if user is logged in
  const isAuthenticated = useSelector((state) => state.auth?.isLoggedIn);
  const isHydrated = useSelector((state) => state.auth?.isHydrated);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      router.push('/home');
    } else {
      router.push('/login');
    }
  };

  const handleGetPricing = () => {
    setIsNavigatingToQuote(true);
    // Route to estimate page (now public)
    router.push('/estimate');
  };

  // Comparison data
  const competitors = [
    {
      name: 'Traditional Courier',
      price: '₹250',
      time: '2-3 days',
      tracking: 'Basic',
    },
    {
      name: 'Express Services',
      price: '₹400',
      time: '24 hours',
      tracking: 'Real-time',
    },
    {
      name: brandingConfig.brandNameFull,
      price: '₹150',
      time: '4-8 hours',
      tracking: 'Live GPS + OTP',
      highlight: true,
    },
  ];

  const stats = [
    { value: '40%', label: 'Cheaper' },
    { value: '4-8hrs', label: 'Delivery' },
    { value: '50K+', label: 'Shipments' },
    { value: '4.8★', label: 'Rating' },
  ];

  const features = [
    {
      icon: <Speed sx={{ fontSize: 28 }} />,
      title: 'Lightning Fast',
      desc: 'Same-day inter-city delivery using our optimized transit network',
    },
    {
      icon: <TrendingDown sx={{ fontSize: 28 }} />,
      title: '40% Cheaper',
      desc: 'Share transport costs with our smart batching system',
    },
    {
      icon: <Security sx={{ fontSize: 28 }} />,
      title: 'Fully Secure',
      desc: 'OTP-verified delivery with real-time GPS tracking',
    },
    {
      icon: <LocalShipping sx={{ fontSize: 28 }} />,
      title: 'Bulk Booking',
      desc: 'Business users can book multiple shipments at once with volume discounts',
    },
  ];

  const howItWorks = [
    {
      step: '01',
      title: 'Book Online',
      desc: 'Enter pickup & drop locations, get instant pricing',
    },
    {
      step: '02',
      title: 'We Pick Up',
      desc: 'Our rider collects your parcel at your doorstep',
    },
    {
      step: '03',
      title: 'Smart Transit',
      desc: 'Parcel travels via our optimized transit points',
    },
    {
      step: '04',
      title: 'Delivered',
      desc: "OTP-verified delivery to recipient's doorstep",
    },
  ];

  return (
    <Box sx={{ bgcolor: COLORS.white, minHeight: '100vh', overflow: 'hidden' }}>
      {/* Navigation */}
      <Box
        component="nav"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          py: 2,
          px: { xs: 2, md: 6 },
          background: scrolled ? 'rgba(255,255,255,0.95)' : 'transparent',
          backdropFilter: scrolled ? 'blur(20px)' : 'none',
          borderBottom: scrolled ? '1px solid #e5e7eb' : 'none',
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            {/* Logo */}
            <Box
              sx={{
                height: scrolled ? 46 : 52,
                width: scrolled ? 140 : 160,
                position: 'relative',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
              }}
              onClick={() => router.push('/')}
            >
              <Image
                src="/assets/terrakite-teal-logo.png"
                alt={brandingConfig.assets.logoAlt}
                fill
                style={{ objectFit: 'contain' }}
                priority
              />
            </Box>

            {/* Desktop Nav */}
            {!isMobile && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Button
                  sx={{
                    color: COLORS.textPrimary,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                  onClick={() =>
                    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
                  }
                >
                  Features
                </Button>
                <Button
                  sx={{
                    color: COLORS.textPrimary,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                  onClick={() =>
                    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })
                  }
                >
                  Pricing
                </Button>
                <Button
                  sx={{
                    color: COLORS.textPrimary,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                  onClick={() =>
                    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })
                  }
                >
                  How it Works
                </Button>
                <Button
                  sx={{
                    color: COLORS.textPrimary,
                    textTransform: 'none',
                    fontWeight: 600,
                  }}
                  onClick={() => router.push('/support')}
                >
                  Support
                </Button>
              </Box>
            )}

            {/* Auth Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {!isMobile && (
                <>
                  {isHydrated && isAuthenticated ? (
                    <Button
                      variant="contained"
                      onClick={() => router.push('/home')}
                      sx={{
                        bgcolor: COLORS.primary,
                        textTransform: 'none',
                        fontWeight: 700,
                        px: 3,
                        borderRadius: 2,
                        boxShadow: 'none',
                        '&:hover': { bgcolor: '#1a1a1a' },
                      }}
                    >
                      Dashboard
                    </Button>
                  ) : (
                    <>
                      <Button
                        variant="outlined"
                        onClick={() => router.push('/login')}
                        sx={{
                          borderColor: COLORS.primary,
                          color: COLORS.primary,
                          textTransform: 'none',
                          fontWeight: 600,
                          borderRadius: 2,
                          '&:hover': {
                            borderColor: COLORS.primary,
                            bgcolor: 'rgba(0,0,0,0.04)',
                          },
                        }}
                      >
                        Login
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => router.push('/login')}
                        sx={{
                          bgcolor: COLORS.primary,
                          textTransform: 'none',
                          fontWeight: 700,
                          px: 3,
                          borderRadius: 2,
                          boxShadow: 'none',
                          '&:hover': { bgcolor: '#1a1a1a' },
                        }}
                      >
                        Get Started
                      </Button>
                    </>
                  )}
                </>
              )}
              {isMobile && (
                <IconButton onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                  {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                </IconButton>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Mobile Menu */}
      {isMobile && mobileMenuOpen && (
        <Fade in>
          <Box
            sx={{
              position: 'fixed',
              top: 70,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: COLORS.white,
              zIndex: 999,
              p: 3,
            }}
          >
            <Stack spacing={2}>
              <Button
                fullWidth
                sx={{ justifyContent: 'flex-start', py: 1.5 }}
                onClick={() => {
                  document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
              >
                Features
              </Button>
              <Button
                fullWidth
                sx={{ justifyContent: 'flex-start', py: 1.5 }}
                onClick={() => {
                  document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                  setMobileMenuOpen(false);
                }}
              >
                Pricing
              </Button>
              <Button
                fullWidth
                sx={{ justifyContent: 'flex-start', py: 1.5 }}
                onClick={() => {
                  router.push('/support');
                  setMobileMenuOpen(false);
                }}
              >
                Support
              </Button>
              <Box sx={{ pt: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleGetStarted}
                  sx={{ bgcolor: COLORS.primary, py: 1.5, borderRadius: 2 }}
                >
                  {isAuthenticated ? 'Go to Dashboard' : 'Get Started'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Fade>
      )}

      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 14, md: 18 },
          pb: { xs: 6, md: 12 },
          background: `linear-gradient(180deg, ${COLORS.surface} 0%, ${COLORS.white} 100%)`,
        }}
      >
        <Container
          maxWidth={false}
          sx={{ px: { xs: 4, sm: 2, md: 3 }, mx: isMobile ? '2%' : '10%' }}
        >
          <Grid container spacing={{ xs: 2, sm: 3, md: 6 }} alignItems="center">
            <Grid item xs={12} sm={7} md={6} order={{ xs: 1, md: 1 }}>
              <Fade in timeout={800}>
                <Box>
                  <Chip
                    label="🚀 Now serving 50+ cities"
                    sx={{
                      bgcolor: COLORS.accentLight,
                      color: COLORS.accent,
                      fontWeight: 600,
                      mb: 3,
                      fontSize: '0.85rem',
                    }}
                  />
                  <Typography
                    variant="h1"
                    sx={{
                      fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
                      fontWeight: 900,
                      color: COLORS.textPrimary,
                      lineHeight: 1.1,
                      letterSpacing: '-2px',
                      mb: 3,
                    }}
                    textAlign={isMobile ? 'center' : 'inherit'}
                  >
                    Delivery at
                    <Box component="span" sx={{ color: COLORS.accent }}>
                      {' '}
                      40% less
                    </Box>
                    <br />
                    in half the time
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                      color: COLORS.textSecondary,
                      fontWeight: 400,
                      mb: 4,
                      fontSize: { xs: '1rem', md: '1.15rem' },
                      maxWidth: 480,
                    }}
                  >
                    Our smart network connects you to vehicles already on the move. Same-day
                    inter-city delivery, without the premium price.
                  </Typography>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    sx={{
                      justifyContent: { xs: 'center', md: 'flex-start' },
                      alignItems: 'center',
                    }}
                  >
                    <Button
                      variant="contained"
                      size="large"
                      endIcon={isNavigatingToQuote ? null : <ArrowForward />}
                      onClick={handleGetPricing}
                      disabled={isNavigatingToQuote}
                      sx={{
                        bgcolor: COLORS.primary,
                        textTransform: 'none',
                        fontWeight: 700,
                        px: 4,
                        py: 1.75,
                        fontSize: '1.05rem',
                        borderRadius: 2,
                        boxShadow: 'none',
                        minWidth: { xs: '100%', sm: 200 },
                        '&:hover': {
                          bgcolor: '#1a1a1a',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
                        },
                        '&:disabled': { bgcolor: '#6b7280' },
                      }}
                    >
                      {isNavigatingToQuote ? (
                        <CircularProgress size={24} sx={{ color: '#fff' }} />
                      ) : (
                        'Get a Quote'
                      )}
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      startIcon={<PlayArrow />}
                      sx={{
                        borderColor: COLORS.primary,
                        color: COLORS.primary,
                        textTransform: 'none',
                        fontWeight: 600,
                        px: 4,
                        py: 1.75,
                        borderRadius: 2,
                        '&:hover': {
                          borderColor: COLORS.primary,
                          bgcolor: 'rgba(0,0,0,0.04)',
                        },
                      }}
                    >
                      Watch Demo
                    </Button>
                  </Stack>
                </Box>
              </Fade>
            </Grid>
            <Grid item xs={12} sm={5} md={6} order={{ xs: 2, md: 2 }}>
              <Slide in direction="left" timeout={1000}>
                <Box
                  sx={{
                    position: 'relative',
                    p: 0,
                  }}
                >
                  {/* Hero Truck Image */}
                  {!isMobile && (
                    <Box
                      sx={{
                        position: 'relative',
                        width: '100%',
                        height: { xs: 180, sm: 300, md: 400 },
                        borderRadius: { xs: 2, md: 4 },
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: COLORS.surface,
                      }}
                    >
                      <Image
                        src="/assets/images/truck4.png"
                        alt="Delivery Truck"
                        fill
                        sizes="(max-width: 600px) 100vw, (max-width: 900px) 50vw, 600px"
                        style={{
                          objectFit: 'contain',
                          objectPosition: 'center',
                        }}
                      />
                    </Box>
                  )}
                </Box>
              </Slide>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: COLORS.surface }}>
        <Container maxWidth="lg">
          <Slide in direction="up" timeout={700}>
            <Box>
              <Grid container spacing={2} justifyContent="center">
                {stats.map((stat, i) => (
                  <Grid item xs={6} sm={3} key={i}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        textAlign: 'center',
                        borderRadius: 2,
                        border: '1px solid #e5e7eb',
                        transition: 'all 0.3s ease',
                        bgcolor: 'rgba(255,255,255,0.9)',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
                        },
                      }}
                    >
                      <Typography
                        variant="h4"
                        sx={{
                          fontWeight: 900,
                          color: i === 0 ? COLORS.accent : COLORS.textPrimary,
                          fontSize: { xs: '1.25rem', md: '1.75rem' },
                          letterSpacing: '-1px',
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: COLORS.textSecondary,
                          fontWeight: 500,
                          fontSize: '0.9rem',
                        }}
                      >
                        {stat.label}
                      </Typography>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Slide>
        </Container>
      </Box>

      {/* Scroll indicator */}
      <Box sx={{ textAlign: 'center', py: 2 }}>
        <IconButton
          onClick={() =>
            document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })
          }
        >
          <KeyboardArrowDown sx={{ fontSize: 32, color: COLORS.textSecondary }} />
        </IconButton>
      </Box>

      {/* Features Section */}
      <Box id="features" sx={{ py: { xs: 8, md: 12 }, bgcolor: COLORS.white }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                color: COLORS.textPrimary,
                fontSize: { xs: '2rem', md: '2.75rem' },
                letterSpacing: '-1px',
                mb: 2,
              }}
            >
              {brandingConfig.messages.whyChooseUs}
            </Typography>
            <Typography
              sx={{
                color: COLORS.textSecondary,
                fontSize: '1.1rem',
                maxWidth: 600,
                mx: 'auto',
              }}
            >
              We've reimagined logistics from the ground up to give you the best of both worlds:
              speed and savings.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature, i) => (
              <Grid item xs={12} md={4} key={i}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 4,
                    border: '1px solid #e5e7eb',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: COLORS.accent,
                      transform: 'translateY(-8px)',
                      boxShadow: '0 20px 60px rgba(16,185,129,0.12)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 3,
                      bgcolor: COLORS.accentLight,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: COLORS.accent,
                      mb: 3,
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 700, color: COLORS.textPrimary, mb: 1.5 }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography sx={{ color: COLORS.textSecondary, lineHeight: 1.7 }}>
                    {feature.desc}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Pricing Comparison */}
      <Box id="pricing" sx={{ py: { xs: 8, md: 12 }, bgcolor: COLORS.surface }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                color: COLORS.textPrimary,
                fontSize: { xs: '2rem', md: '2.75rem' },
                letterSpacing: '-1px',
                mb: 2,
              }}
            >
              Compare & Save
            </Typography>
            <Typography sx={{ color: COLORS.textSecondary, fontSize: '1.1rem' }}>
              See how we stack up against traditional delivery options
            </Typography>
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {competitors.map((comp, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    border: comp.highlight ? `2px solid ${COLORS.accent}` : '1px solid #e5e7eb',
                    bgcolor: COLORS.white,
                    position: 'relative',
                    transform: comp.highlight ? 'scale(1.05)' : 'scale(1)',
                    zIndex: comp.highlight ? 1 : 0,
                  }}
                >
                  {comp.highlight && (
                    <Chip
                      label="Best Value"
                      sx={{
                        position: 'absolute',
                        top: -12,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        bgcolor: COLORS.accent,
                        color: '#fff',
                        fontWeight: 700,
                      }}
                    />
                  )}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: COLORS.textPrimary, mb: 3 }}
                  >
                    {comp.name}
                  </Typography>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 900,
                      color: comp.highlight ? COLORS.accent : COLORS.textPrimary,
                      mb: 3,
                      letterSpacing: '-1px',
                    }}
                  >
                    {comp.price}
                    <Typography
                      component="span"
                      sx={{
                        fontSize: '1rem',
                        fontWeight: 400,
                        color: COLORS.textSecondary,
                      }}
                    >
                      /10kg
                    </Typography>
                  </Typography>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CheckCircle
                        sx={{
                          color: comp.highlight ? COLORS.accent : COLORS.textSecondary,
                          fontSize: 20,
                        }}
                      />
                      <Typography sx={{ color: COLORS.textSecondary }}>
                        {comp.time} delivery
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <CheckCircle
                        sx={{
                          color: comp.highlight ? COLORS.accent : COLORS.textSecondary,
                          fontSize: 20,
                        }}
                      />
                      <Typography sx={{ color: COLORS.textSecondary }}>
                        {comp.tracking} tracking
                      </Typography>
                    </Box>
                  </Stack>
                  {comp.highlight && (
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleGetPricing}
                      sx={{
                        mt: 4,
                        bgcolor: COLORS.accent,
                        textTransform: 'none',
                        fontWeight: 700,
                        py: 1.5,
                        borderRadius: 2,
                        boxShadow: 'none',
                        '&:hover': { bgcolor: '#059669' },
                      }}
                    >
                      Get Started
                    </Button>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How it Works */}
      <Box id="how-it-works" sx={{ py: { xs: 8, md: 12 }, bgcolor: COLORS.white }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                color: COLORS.textPrimary,
                fontSize: { xs: '1.75rem', md: '2.75rem' },
                letterSpacing: '-1px',
                mb: 2,
              }}
            >
              How it works
            </Typography>
            <Typography
              sx={{
                color: COLORS.textSecondary,
                fontSize: { xs: '0.95rem', md: '1.1rem' },
                px: { xs: 2, md: 0 },
              }}
            >
              Get your parcel delivered in 4 simple steps
            </Typography>
          </Box>

          <Grid container spacing={{ xs: 3, md: 4 }}>
            {howItWorks.map((step, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box sx={{ textAlign: 'center', px: { xs: 2, sm: 1 } }}>
                  <Box
                    sx={{
                      width: { xs: 72, md: 80 },
                      height: { xs: 72, md: 80 },
                      borderRadius: '50%',
                      border: `3px solid ${COLORS.accent}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto',
                      mb: 2,
                      bgcolor: i % 2 === 0 ? `${COLORS.accent}15` : COLORS.white,
                      position: 'relative',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: '1.5rem', md: '1.75rem' },
                        fontWeight: 800,
                        color: COLORS.accent,
                      }}
                    >
                      {i + 1}
                    </Typography>
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: COLORS.textPrimary,
                      mb: 1,
                      fontSize: { xs: '1.1rem', md: '1.25rem' },
                    }}
                  >
                    {step.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: COLORS.textSecondary,
                      fontSize: { xs: '0.875rem', md: '0.95rem' },
                      lineHeight: 1.6,
                    }}
                  >
                    {step.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: COLORS.primary }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 800,
                color: COLORS.white,
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                letterSpacing: '-1px',
                mb: 2,
              }}
            >
              Ready to ship smarter?
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.1rem', mb: 4 }}>
              Join thousands of businesses saving time and money with {brandingConfig.brandNameFull}
            </Typography>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForward />}
              onClick={handleGetStarted}
              sx={{
                bgcolor: COLORS.accent,
                textTransform: 'none',
                fontWeight: 700,
                px: 5,
                py: 2,
                fontSize: '1.1rem',
                borderRadius: 2,
                '&:hover': { bgcolor: '#059669' },
              }}
            >
              {isAuthenticated ? 'Go to Dashboard' : 'Start Shipping'}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ py: 4, bgcolor: COLORS.surface, borderTop: '1px solid #e5e7eb' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <LocalShipping sx={{ color: COLORS.primary }} />
              <Typography sx={{ fontWeight: 700, color: COLORS.textPrimary }}>
                {brandingConfig.brandNameFull}
              </Typography>
            </Box>
            <Typography sx={{ color: COLORS.textSecondary, fontSize: '0.9rem' }}>
              {brandingConfig.legal.copyright}
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
